import React, { CSSProperties, useMemo } from 'react';
import styled from 'styled-components';
import { Language, Product } from 'types/schema';
import calculateVAT from '../../../utils/calculateVAT';
import formatMoney from '../../../utils/formatMoney';
import { getLimitedTimeOfferPercent } from 'utils/discount';
import media from 'utils/mediaStyle';

export const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0 4px;
  width: ${({ isDiscount, hasHorizontalPadding }) =>
    isDiscount ? (hasHorizontalPadding ? '142px' : '152') : 'fit-content'};
  ${media.md`
    width: ${({ isDiscount, hasHorizontalPadding }) => (isDiscount ? (hasHorizontalPadding ? '227' : '235px') : 'fit-content')};
  `}
`;

export const PriceText = styled.p`
  color: #595959;
  font-size: 16px;
  font-weight: 700;
`;

const VAT = styled.p`
  font-family: 'Inter';
  font-size: 12px;
  font-weight: 500;
  color: #585a60;
  font-style: italic;
`;

export interface PriceProps {
  style?: CSSProperties;
  product: Product;
  baseUom?: string;
  baseUomPrice?: number;
  netUom?: string;
  uom?: string;
  lang?: Language;
  isShowPriceUom?: boolean;
  hasHorizontalPadding?: boolean;
}

const PriceContainer = styled.div`
  display: flex;
  align-items: ${({ isDiscount }) => (isDiscount ? 'flex-start' : 'center')};
  column-gap: 4px;
  flex-wrap: wrap;
  flex-direction: ${({ isDiscount }) => (isDiscount ? 'column' : 'row')};
`;

const DiscountPrice = styled.p`
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #e2483c;
  flex-shrink: 0;
  @media (max-width: 991px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const PriceEl = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  line-height: 24px;
  @media (max-width: 991px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const DiscountTag = styled.div`
  width: fit-content;
  padding: 0px 4px;
  background: #fef2f2;
  border-radius: 2px;
  color: #e2483c;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  flex-shrink: 0;
`;

const PerPrice = styled.p`
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  line-height: 18px;
  color: #111827;
`;

const BasePrice = styled.p`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #6b7280;
  text-decoration: line-through;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Price = ({ product, baseUom, netUom, hasHorizontalPadding }: PriceProps) => {
  const { baseUnit, netUnit, priceV2, baseUomPrice, netUomPrice } = product;
  const isDiscount = Number(priceV2?.originalPrice) > Number(priceV2?.price);
  const renderUomPrice = useMemo(() => {
    if (baseUomPrice && baseUnit !== 1) {
      return (
        <PerPrice>
          ({formatMoney(calculateVAT(baseUomPrice, priceV2?.vat, priceV2?.vatIncluded).beforeVat, 1)}
          <span style={{ textDecoration: 'underline' }}>đ</span>/
          <span style={{ textTransform: 'capitalize' }}>{baseUom}</span>)
        </PerPrice>
      );
    }

    if (netUomPrice && netUnit !== 1) {
      return (
        <PerPrice>
          ({formatMoney(calculateVAT(netUomPrice, priceV2?.vat, priceV2?.vatIncluded).beforeVat, 1)}
          <span style={{ textDecoration: 'underline' }}>đ</span>/
          <span style={{ textTransform: 'capitalize' }}>{netUom}</span>)
        </PerPrice>
      );
    }
    return '';
  }, [baseUomPrice, baseUom, netUomPrice, netUom]);

  return (
    <PriceContainer isDiscount={isDiscount}>
      <PriceRow isDiscount={isDiscount} hasHorizontalPadding={hasHorizontalPadding}>
        {isDiscount ? (
          <>
            <DiscountPrice>
              {formatMoney(calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat)}
              <span style={{ fontSize: 14, textDecoration: 'underline' }}>đ</span>
            </DiscountPrice>
            <BasePrice>
              {formatMoney(calculateVAT(priceV2?.originalPrice, priceV2?.vat, priceV2?.vatIncluded).beforeVat)}
              <span style={{ fontSize: 12, textDecoration: 'underline' }}>đ</span>
            </BasePrice>
            <DiscountTag>{getLimitedTimeOfferPercent(priceV2?.originalPrice || 0, priceV2?.price || 0)}</DiscountTag>
          </>
        ) : (
          <PriceEl>
            {formatMoney(calculateVAT(priceV2?.price, priceV2?.vat, priceV2?.vatIncluded).beforeVat)}
            <span style={{ fontSize: 14, textDecoration: 'underline' }}>đ</span>
          </PriceEl>
        )}
      </PriceRow>
      {window.location.pathname !== '/' && renderUomPrice}
    </PriceContainer>
  );
};
